import { Injectable } from '@angular/core';
import { HttpClient, HttpParameterCodec, HttpParams, HttpResponse } from '@angular/common/http';
import { ApplicationErreur } from '../exception/ApplicationErreur';

@Injectable({
	providedIn: 'root'
})
export class BrowserService {

	constructor(private http: HttpClient) {
	}

	context(): string {
		const context = window.location.pathname.split('/')[1];
		if (context.indexOf('.html') >= 0) {
			return '/';
		}
		return '/' + context;
	}

	path(): string {
		let context = this.context();
		if (context.charAt(context.length - 1) === '/') {
			context = context.substr(0, context.length - 1);
		}
		return context;
	}

	url(path: string, prefix?: string): string {
		let p = prefix;
		if (!p) {
			p = '/ws';
		}

		return this.path() + p + path;
	}

	appelerEnGetEtOuvrirPdf(url: string, callbackSuccesFn: any = null, callbackErrorFn: any = null): void {
		this.http.get(this.url(url), { observe: 'response', responseType: 'arraybuffer' }).subscribe(response => {
			this.ouvrirPdf(response);
			if (callbackSuccesFn) {
				callbackSuccesFn();
			}
		}, error => {
			if (callbackErrorFn) {
				callbackErrorFn(error);
			}
		});
	}

	appelerEnPostEtOuvrirPdf(url: string, httpParams: HttpParams, callbackSuccesFn: any, callbackErrorFn: any): void {
		this.http.post(this.url(url), httpParams, { observe: 'response', responseType: 'arraybuffer' }).subscribe(response => {
			this.ouvrirPdf(response);
			if (callbackSuccesFn) {
				callbackSuccesFn();
			}
		}, error => {
			if (callbackErrorFn) {
				callbackErrorFn(error);
			}
		});
	}

	ouvrirPdf(response: any): void {
		const data = window.URL.createObjectURL(new Blob([response.body], { type: response.headers.get('content-type') }));
		const link = document.createElement('a');
		link.href = data;
		link.target = '_blank';
		link.click();
	}

	/**
	 * Appel en GET l'URL et télécharge le fichier reçu.
	 * Si le fichier est un pdf, on ouvre un nouvel onglet pour l'afficher.
	 *
	 * @param url URL de récupération du fichier.
	 * @param callbackSuccesFn fonction exécutée en cas de succès du téléchargement.
	 * @param callbackErrorFn fonction exécutée en cas d'échec du téléchargement.
	 * @param urlForcee URL forcée à utiliser à la place de l'URL construite.
	 */
	appelerEnGetEtTelechargerFichier(url: string, callbackSuccesFn: Function = null, callbackErrorFn: Function = null, urlForcee?: string): void {
		this.http.get(urlForcee || this.url(url), { observe: 'response', responseType: 'arraybuffer' })
			.subscribe(response => {
				this.telechargerFichier(response);
				if (callbackSuccesFn) {
					callbackSuccesFn();
				}
			}, error => {
				if (callbackErrorFn) {
					callbackErrorFn(this.transformerReponseEnErreur(error));
				}
			});
	}

	/**
	 * Déclenche le téléchargement du fichier présent dans la réponse Http.
	 * Si le fichier est un pdf, on ouvre un nouvel onglet pour l'afficher.
	 * @param response la réponse HTTP.
	 */
	telechargerFichier(response: HttpResponse<ArrayBuffer>): void {
		const filenameAttribute = 'filename=';
		const contentType = response.headers?.get('content-type');

		const isPDF = contentType?.indexOf('pdf') !== -1;
		if (isPDF) {
			this.ouvrirPdf(response);
			return;
		}

		const contentDisposition = response.headers?.get('content-disposition');
		const filenameIndex = contentDisposition?.indexOf(filenameAttribute);
		if (filenameIndex === -1) {
			console.error('Impossible de télécharger le fichier : il n\'est pas présent dans la réponse.', response);
			return;
		}

		const data = window.URL.createObjectURL(new Blob([response.body], { type: response.headers.get('content-type') }));
		const anchor = document.createElement('a');
		anchor.href = data;
		anchor.target = '_blank';
		anchor.download = contentDisposition.substring(filenameIndex + filenameAttribute.length);

		anchor.click();
	}

	/**
	 * Crypter le numéro de bon de facturation
	 *
	 * @param id identifiant de bon de livraison
	 * @param loginUtilisateur le login de l'utilisateur
	 * @returns string de bon de livraison crypté
	 */
	crypter(id: number, loginUtilisateur: string) {
		return btoa(btoa(id.toString()) + '-' + loginUtilisateur);
	}

	/**
	 * Permet de créer et d'ouvrir un pdf à partir d'une chaine Base64.
	 *
	 * @param base64 La chaîne Base64.
	 */
	creerEtOuvrirPdfBase64(base64: string): void {
		const byteArray = new Uint8Array(atob(base64).split('').map(char => char.charCodeAt(0)));

		const data = window.URL.createObjectURL(new Blob([byteArray], { type: 'application/pdf' }));
		const link = document.createElement('a');
		link.href = data;
		link.target = '_blank';
		link.click();
	}

	/**
	 * Permet de télécharger un fichier CSV.
	 *
	 * @param response La response.
	 */
	telechargerCsv(response: any): void {
		const contentDisposition = response.headers.get('Content-Disposition');
		const fileName = contentDisposition.split(';')[1].split('filename')[1].split('=')[1].trim() + '.csv';
		const data = window.URL.createObjectURL(new Blob([response.body], { type: 'application/csv' }));
		const link = document.createElement('a');
		link.href = data;
		link.target = '_blank';
		link.download = fileName;
		link.click();
	}

	/**
	 * Permet de traiter les erreurs http.
	 *
	 * @param response la réponse de l'erreur à traiter.
	 * @return l'erreur traitée convertie en format Json.
	 */
	transformerReponseEnErreur(response: any): ApplicationErreur {
		const type = response.headers.get('content-type');
		if (!type.startsWith('application/json')) {
			return response;
		}
		const decoder = new TextDecoder('utf-8');
		const domString = decoder.decode(response.error);
		return JSON.parse(domString);
	}
}

class CustomHttpParamEncoder implements HttpParameterCodec {
	encodeKey(key: string): string {
		return encodeURIComponent(key);
	}

	encodeValue(value: string): string {
		return encodeURIComponent(value);
	}

	decodeKey(key: string): string {
		return decodeURIComponent(key);
	}

	decodeValue(value: string): string {
		return decodeURIComponent(value);
	}
}
